import { ApolloClient, NormalizedCacheObject, ApolloLink, createHttpLink, split } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import DebounceLink from 'apollo-link-debounce'
import { RestLink } from 'apollo-link-rest'
import onErrorLink from '../graphql/error-handlers'
import cache from '../graphql/cache'
import { getESAPIHost } from './host'
import { GRAPHQL_ENDPOINTS, GraphQLContextWithEndpoint } from './graphql-endpoints'
import { coerceZipCode } from '../utils/zip-code'

export { GRAPHQL_ENDPOINTS } from './graphql-endpoints'
export type { GraphQLContextWithEndpoint } from './graphql-endpoints'
export { DEFAULT_DEBOUNCE_TIME_IN_MS }

const DEFAULT_DEBOUNCE_TIME_IN_MS = 300

const httpLink = createHttpLink( {
	uri: `${getESAPIHost()}/inventory/graphql`,
} )

const costCaptureLink = createHttpLink( {
	uri: `${getESAPIHost()}/things/federated/graphql`,
} )

const getAuthLink = ( accessToken: string ) => setContext( ( _, { headers } ) => {
	return {
		headers: {
			...headers,
			authorization: `Bearer ${accessToken}`,
		},
	}
} )

const restLink = new RestLink( {
	uri: getESAPIHost(),
	// ESDB stores branch zip codes as integers, so the /branches REST response
	// returns zip_code as a number and strips leading zeros (02134 -> 2134).
	// Coerce it back to a padded string here, at the single point every branch
	// consumer reads through, so no display or form site has to repair it.
	typePatcher: {
		BranchPayload: ( data: any ) => {
			if ( data?.location?.zip_code != null ) {
				data.location.zip_code = coerceZipCode( data.location.zip_code )
			}
			return data
		},
	},
} )

export default function getApolloClient( accessToken: string, showError: any ): ApolloClient<NormalizedCacheObject> {
	const authLink = getAuthLink( accessToken )

	// Split link to route queries to appropriate GraphQL services based on context
	const splitLink = split(
		( { getContext } ) => {
			const context = getContext() as GraphQLContextWithEndpoint
			// Route to external service if context specifies CostCapture endpoint
			return context.endpoint === GRAPHQL_ENDPOINTS.COST_CAPTURE
		},
		costCaptureLink, // Use external service for CostCapture endpoint
		httpLink, // Use inventory service for all other queries (default)
	)

	return new ApolloClient( {
		link: ApolloLink.from( [
			new DebounceLink( DEFAULT_DEBOUNCE_TIME_IN_MS ),
			onErrorLink( showError ),
			authLink,
			restLink,
			splitLink,
		] ),
		cache,
	} )
}
